/**
 * Deep Dive — Variation B (architecture-first).
 *
 * Layout (top to bottom):
 *   Hero          — eyebrow + huge album title + meta + movement framing
 *   Arch hero     — ArchCurve, three-up apertura/centro/cierre
 *   §01 Forma     — parece / se mueve essay panel (full measure)
 *   §02 Tensiones — single-tension deck carousel
 *   §03 Momentos  — single-moment deck carousel
 *   Enduring      — standalone closing paragraph (deepDive.enduring)
 *
 * §04 Posición y duración (the prev/aquí/después spine via DiscographySpine)
 * and the §01 sticky `TrackSidebar` are intentionally not rendered. Their
 * schema fields stay populated and their component files remain on disk
 * (DiscographySpine.tsx, TrackSidebar.tsx) so either can be reintroduced
 * without a data migration; for now the deep dive closes on the enduring
 * paragraph as a bridge into §05 Significado.
 */

import { Reveal } from '../primitives/Reveal';
import { ArchCurve } from './ArchCurve';
import { MomentsCarousel } from './MomentsCarousel';
import { TensionsCarousel } from './TensionsCarousel';
import { fmtTotalRuntime } from '@/lib/fmtDur';
import type { AvailableAlbumModule } from '@/types/album';

interface DeepDiveProps {
  album: AvailableAlbumModule;
}

function getHingeTrack(tracks: AvailableAlbumModule['deepDive']['tracks']) {
  const idx = tracks.findIndex((t) => t.hinge);
  const track = idx >= 0 ? tracks[idx] : undefined;
  return { n: track?.n, title: track?.title };
}

/**
 * Pull the first sentence of `actual_behavior` into a "Se mueve como"
 * lead, and return the remainder as the essay body so the same sentence
 * doesn't appear twice in §01. If the behavior is a single sentence the
 * essay body comes back empty and the caller skips rendering it.
 *
 * Heuristic: split on the first `". "` boundary. Both shipped albums
 * (OK Computer, Lateralus) follow that pattern; if a future album uses
 * abbreviations like "No. 7" we'll harden this with a capital-letter
 * lookahead — not worth the complexity for data we can audit.
 */
function splitMovement(actualBehavior: string): { lead: string; rest: string } {
  const idx = actualBehavior.indexOf('. ');
  if (idx === -1) return { lead: actualBehavior, rest: '' };
  return {
    lead: actualBehavior.slice(0, idx + 1),
    rest: actualBehavior.slice(idx + 2),
  };
}

export function DeepDive({ album }: DeepDiveProps): JSX.Element {
  const { library, deepDive } = album;
  const totalMs = deepDive.tracks.reduce((sum, t) => sum + t.ms, 0);
  const hinge = getHingeTrack(deepDive.tracks);
  const movement = splitMovement(deepDive.actual_behavior);

  return (
    <section className="dd-page" id="deep-dive" aria-labelledby="deep-dive-title">
      <div className="dd-noise" aria-hidden="true" />

      <div className="dd-measure">
        <Reveal as="header" className="dd-hero">
          <div className="dd-eyebrow">Disco analizado</div>
          <h2 className="dd-album-title" id="deep-dive-title">
            {deepDive.title}
          </h2>
          <div className="dd-album-meta">
            <span>{deepDive.artist}</span>
            <span className="dot">·</span>
            <span>{deepDive.year}</span>
            <span className="dot">·</span>
            <span>
              {deepDive.tracks.length} pistas · {fmtTotalRuntime(totalMs)}
            </span>
            <span className="pos">Cómo se mueve el álbum</span>
          </div>
        </Reveal>

        <Reveal as="section" className="dd-arch-hero">
          <div className="dd-arch-hero-eyebrow">
            {hinge.title
              ? `${hinge.title} es el giro central`
              : ''}
          </div>
          <div className="dd-arch-v2">
            <div className="dd-arch-chart">
              <ArchCurve tracks={deepDive.tracks} />
            </div>
            <div className="dd-arch-cols dd-arch-cols-rich">
              <div className="col">
                <div className="head">
                  <span className="roman">I</span>
                  <span>Apertura</span>
                </div>
                <div className="body">{deepDive.arch_opening}</div>
              </div>
              <div className="col">
                <div className="head">
                  <span className="roman">II</span>
                  <span>Centro</span>
                </div>
                <div className="body">{deepDive.arch_middle}</div>
              </div>
              <div className="col">
                <div className="head">
                  <span className="roman">III</span>
                  <span>Cierre</span>
                </div>
                <div className="body">{deepDive.arch_closing}</div>
              </div>
            </div>
          </div>
          <p className="dd-arch-caption">{deepDive.arch_sequencing_note}</p>
        </Reveal>

        <Reveal as="div" className="dd-section-head">
          <div className="dd-section-num">
            <span className="num">01</span>Forma y comportamiento
          </div>
          <h3 className="dd-section-title">
            Lo que parece, y lo que pasa cuando lo escuchas de corrido.
          </h3>
        </Reveal>

        <Reveal as="div" className="dd-pm-block">
          <div className="dd-pm">
            <div>
              <div className="col-eyebrow parece">Parece</div>
              <p className="col-text italic">{deepDive.apparent_form}</p>
            </div>
            <div>
              <div className="col-eyebrow se-mueve">Se mueve como</div>
              <p className="col-text solid">{movement.lead}</p>
            </div>
          </div>
          {movement.rest ? <p className="dd-pm-essay">{movement.rest}</p> : null}
        </Reveal>

        <Reveal as="div" className="dd-section-head">
          <div className="dd-section-num">
            <span className="num">02</span>Tensiones centrales
          </div>
          <h3 className="dd-section-title">
            Tres fuerzas que el disco nunca termina de resolver.
          </h3>
        </Reveal>

        <Reveal as="div">
          <TensionsCarousel tensions={deepDive.tensions} />
        </Reveal>

        <Reveal as="div" className="dd-section-head">
          <div className="dd-section-num">
            <span className="num">03</span>Momentos clave
          </div>
          <h3 className="dd-section-title">Dos puntos donde el disco cambia de escala.</h3>
        </Reveal>

        <Reveal as="div">
          <MomentsCarousel moments={deepDive.signature_moments} />
        </Reveal>

        <Reveal as="p" className="dd-enduring">
          {deepDive.enduring}
        </Reveal>
      </div>
      <span className="sr-only">Fin del deep dive de {library.album}.</span>
    </section>
  );
}
